/**
 * Typed Firestore collection and document references for the frontend.
 *
 * Every ref has a converter attached so reads return fully-typed objects
 * with `oid` injected from the document ID, and writes automatically strip `oid`.
 *
 * Usage:
 *   const q = query(connectionsRef(uid), where("status", "==", "connected"))
 *   const snap = await getDoc(connectionRef(uid, connectionId))  // snap.data() is Connection
 */
import {
  ACCOUNTS_SUBCOL,
  APPS_COL,
  CONNECTIONS_SUBCOL,
  CONNECTION_SECRETS_SUBCOL,
  CONNECTORS_COL,
  TRANSACTIONS_SUBCOL,
  USERS_COL,
} from "@shared/collections"
import type { Account, Connection, Connector } from "@shared/types/connection"
import type { Transaction } from "@shared/types/transaction"
import { collection, doc } from "firebase/firestore"

import { db } from "../firebase"
import { makeConverter } from "./converter"

// /connectors
export const connectorsRef = () =>
  collection(db, CONNECTORS_COL).withConverter(makeConverter<Connector>())

// /users/{uid}/connections
export const connectionsRef = (uid: string) =>
  collection(db, USERS_COL, uid, CONNECTIONS_SUBCOL).withConverter(
    makeConverter<Connection>(),
  )

export const connectionRef = (uid: string, connectionId: string) =>
  doc(db, USERS_COL, uid, CONNECTIONS_SUBCOL, connectionId).withConverter(
    makeConverter<Connection>(),
  )

// /users/{uid}/connections/{connectionId}/accounts
export const accountsRef = (uid: string, connectionId: string) =>
  collection(
    db,
    USERS_COL,
    uid,
    CONNECTIONS_SUBCOL,
    connectionId,
    ACCOUNTS_SUBCOL,
  ).withConverter(makeConverter<Account>())

export const accountRef = (
  uid: string,
  connectionId: string,
  accountId: string,
) =>
  doc(
    db,
    USERS_COL,
    uid,
    CONNECTIONS_SUBCOL,
    connectionId,
    ACCOUNTS_SUBCOL,
    accountId,
  ).withConverter(makeConverter<Account>())

// /users/{uid}/connections/{connectionId}/accounts/{accountId}/transactions
export const transactionsRef = (
  uid: string,
  connectionId: string,
  accountId: string,
) =>
  collection(
    db,
    USERS_COL,
    uid,
    CONNECTIONS_SUBCOL,
    connectionId,
    ACCOUNTS_SUBCOL,
    accountId,
    TRANSACTIONS_SUBCOL,
  ).withConverter(makeConverter<Transaction>())

// Intentionally omitted: connection_secrets — no client read access per Firestore rules
// Intentionally omitted: apps — not yet used
export { APPS_COL, CONNECTION_SECRETS_SUBCOL }
